import {
  IconBriefcase,
  IconCash,
  IconExternalLink,
  IconFileText,
  type IconProps,
  IconUsers,
} from '@tabler/icons-react';
import type { FC, ForwardRefExoticComponent, RefAttributes } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ClientStatsData, getClientStats } from '@/api/client-stats';
import type { ClientRow } from '@/components/clients-table';
import { DetailInfoCell } from '@/components/detail-page-primitives';
import { type ClientStatus, StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  focusDialogOnOpen,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  client: ClientRow | null;
}

const formatCurrency = (cents: number): string => {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

type TablerIcon = ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;

const QuickStat: FC<{ icon: TablerIcon; label: string; value: string }> = ({
  icon: Icon,
  label,
  value,
}) => (
  <Card className="py-0">
    <CardContent className="flex flex-col gap-1.5 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <span className="text-lg font-semibold tabular-nums tracking-tight text-foreground">
        {value}
      </span>
    </CardContent>
  </Card>
);

/**
 * Lightweight read-only quick view of a client shown from the Clients table
 * quick-actions menu. Surfaces basic info plus headline stats, with a button to
 * open the full client detail page. Internal-only (stats endpoint is gated).
 */
export const ClientQuickViewModal: FC<Props> = ({ open, onClose, client }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ClientStatsData | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  useEffect(() => {
    if (!open || !client?.id) {
      setStats(null);
      return;
    }
    let cancelled = false;
    setIsLoadingStats(true);
    setStats(null);
    getClientStats(client.id)
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingStats(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, client?.id]);

  if (!client) return null;

  const handleViewDetails = () => {
    onClose();
    navigate(`/clients/${client.id}`);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-2xl" onOpenAutoFocus={focusDialogOnOpen}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5 pr-6">
            <IconBriefcase className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="truncate">{client.name}</span>
            {client.status && <StatusChip status={client.status as ClientStatus} type="client" />}
          </DialogTitle>
          <DialogDescription>
            {client.clientCode ? `${client.clientCode} · ` : ''}
            {client.email || 'No email on file'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <DetailInfoCell label="Primary Contact" value={client.primaryContactName || '—'} />
            <DetailInfoCell label="Contact Phone" value={client.primaryContactPhone || '—'} />
            <DetailInfoCell label="Client Code" value={client.clientCode || '—'} />
            <DetailInfoCell
              label="Created"
              value={client.createdAt ? formatDate(client.createdAt) : '—'}
            />
          </div>

          {isLoadingStats ? (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-[68px] w-full rounded-xl" />
              ))}
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              <QuickStat
                icon={IconCash}
                label="Total Volume"
                value={formatCurrency(stats.totalVolumeAllTimeCents)}
              />
              <QuickStat
                icon={IconCash}
                label="Payments"
                value={stats.totalPaymentsAllTime.toLocaleString()}
              />
              <QuickStat
                icon={IconFileText}
                label="Files"
                value={stats.totalPaymentFilesAllTime.toLocaleString()}
              />
              <QuickStat
                icon={IconUsers}
                label="Beneficiaries"
                value={stats.totalSuppliers.toLocaleString()}
              />
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleViewDetails}>
            View Full Details
            <IconExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
